import { useId, useRef, useState } from "react";
import type {
  PositionExitEvaluationSpec,
  PositionExitTrigger,
  StructureEntryCandidateLeg,
  StructureEntryEvaluationSpec,
  WatchEvaluationSpec,
} from "@/shared/lib/types";
import styles from "./WatchDetailPanel.module.css";

type CustomerOptionIdentity = {
  ticker?: string;
  expiry?: string;
  strike?: string;
  option_type?: "call" | "put" | "CALL" | "PUT";
};

type MarketSignalCondition = {
  job?: "MARKET";
  signal?: { family?: string; name?: string };
  subject?: { ticker?: string };
  condition?: {
    operator?: "gte" | "lte";
    operator_phrase?: string;
    threshold?: { value?: string; display?: string };
  };
  sentence?: string;
};

type MarketSignalMeasurement = {
  indicator?: string;
  completed_session_display?: string;
  sentence?: string;
  metrics?: Record<string, string>;
};

type MarketRecipeCondition = {
  recipe?: { name?: string; summary?: string };
  conditions?: Array<{ sentence?: string }>;
  evidence_time?: { display?: string };
  sentence?: string;
  behavior?: string;
  schedule?: string;
};

/** Consumer-safe per-record projection attached to an activity item. */
type ActivityItemDetails = {
  evaluation?: { state?: string; summary?: string };
  data_quality?: { state?: string; summary?: string };
  delivery?: { state?: string; summary?: string };
  measurement?: {
    indicator?: string;
    sentence?: string;
    metrics?: Record<string, string>;
    completed_session_display?: string;
    state?: string;
    conditions_met?: number;
    clauses?: Array<{
      clause_id?: string;
      condition_met?: boolean;
      observed_value?: string;
      threshold?: string;
      unit?: string;
    }>;
  };
};

export interface WatchDetail {
  watch: { instrument?: { ticker?: string; option?: CustomerOptionIdentity }; lifecycle?: { state?: string; reason?: string | null; created_at?: number; expires_at?: number }; position?: { strategy?: string; fees_policy?: string; entry_basis?: { type?: string; amount_per_share?: string } | null; valuation?: { state?: string; entry_cash_flow_usd?: string | null; current_close_cash_flow_usd?: string | null; unrealized_pnl_usd?: string | null; legs?: Array<{ option?: CustomerOptionIdentity; role?: string; bid?: string | null; ask?: string | null; selected_mark?: string | null; delta?: string | null; quote_state?: string; quoted_at?: number | null }> }; legs?: Array<{ option?: CustomerOptionIdentity; side?: string; contracts?: number; multiplier?: number; entry_premium_per_share?: string | null; entry_executed_at?: number }> } };
  monitoring_context: {
    evaluation?: { state?: string; occurred_at?: number; summary?: string };
    data_quality?: { state?: string; summary?: string };
    alert_eligibility?: { state?: string; reason?: string; eligible_at?: number };
    delivery?: { state?: string; summary?: string };
    next_evaluation_at?: number | null;
    signal_measurement?: MarketSignalMeasurement;
  };
  activity?: { items?: Array<{ id: string; occurred_at?: number | null; kind: string; title: string; description: string; state: string; details?: ActivityItemDetails }> };
  settings?: { intent?: string; policy?: Record<string, unknown>; conditions?: unknown[]; condition_logic?: string; evaluation_spec?: WatchEvaluationSpec; signal_condition?: MarketSignalCondition; recipe_condition?: MarketRecipeCondition };
  actions?: { can_pause?: boolean; can_resume?: boolean; can_edit?: boolean; can_cancel?: boolean };
}

export interface WatchDetailPanelProps {
  detail?: WatchDetail;
  loading?: boolean;
  error?: string;
  onRefresh: () => void;
  onEdit: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  /** Immutable held positions are copied through routed full-page authoring. */
  onDuplicate?: () => void;
  onReplace?: () => void;
  className?: string;
}

const formatTime = (value?: number | null) => value ? new Date(value * 1000).toLocaleString() : "Not scheduled";
const humanize = (value?: string) => (value || "Unknown").toLowerCase().replace(/_/g, " ");

const ACTIVITY_TONES: Record<string, "good" | "warn" | "bad"> = {
  TRIGGERED: "good",
  CREATED: "good",
  SENT: "good",
  DELIVERED: "good",
  ELIGIBLE: "good",
  FRESH: "good",
  FAILED: "bad",
  DEFERRED: "warn",
  STALE: "warn",
  INSUFFICIENT: "warn",
  INELIGIBLE_DATA: "warn",
  COOLDOWN: "warn",
  WAITING_FOR_RESET: "warn",
  PENDING_RETRY: "warn",
};
const activityTone = (state?: string) => ACTIVITY_TONES[(state || "").toUpperCase()] ?? "muted";

function formatOption(option?: CustomerOptionIdentity) {
  const optionType = option?.option_type?.toLowerCase();
  if (!option?.ticker || !option.expiry || !option.strike || !["call", "put"].includes(optionType || "")) return "Option details unavailable";
  const expiry = new Date(`${option.expiry}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const strike = Number(option.strike).toLocaleString("en-US", { maximumFractionDigits: 3 });
  return `${option.ticker} ${expiry} $${strike} ${optionType === "call" ? "Call" : "Put"}`;
}

function optionIdentityKey(option?: CustomerOptionIdentity) {
  return option ? [option.ticker, option.expiry, option.strike, option.option_type?.toLowerCase()].join("|") : "";
}

function strategyBasisSummary(position: NonNullable<WatchDetail["watch"]["position"]>) {
  const basis = position.entry_basis;
  const amount = Number(basis?.amount_per_share);
  const legs = position.legs || [];
  const quantity = legs[0]?.contracts;
  const multiplier = legs[0]?.multiplier;
  if (!basis || !["NET_DEBIT", "NET_CREDIT"].includes(basis.type || "") || !Number.isFinite(amount) || amount < 0) return null;
  const direction = basis.type === "NET_CREDIT" ? "Received" : "Paid";
  const perShare = `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} per share`;
  if (!quantity || !multiplier || !legs.every((leg) => leg.contracts === quantity && leg.multiplier === multiplier)) return `${direction} ${perShare} across all legs, before fees.`;
  const money = (value: number) => value.toLocaleString("en-US", { style: "currency", currency: "USD" });
  return `${direction} ${perShare} across all legs — ${money(amount * multiplier)} per spread × ${quantity} spread${quantity === 1 ? "" : "s"} = ${money(amount * multiplier * quantity)} total, before fees.`;
}

function formatExpiry(expiry?: string) {
  const match = expiry?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "Not available";
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatStrike(strike?: string) {
  const value = Number(strike);
  return Number.isFinite(value) && value > 0
    ? `$${value.toLocaleString("en-US", { maximumFractionDigits: 3 })}`
    : "Strike unavailable";
}

function formatContractCount(contracts?: number) {
  if (!Number.isInteger(contracts) || !contracts || contracts < 1) return "Contracts unavailable";
  return `${contracts} ${contracts === 1 ? "contract" : "contracts"}`;
}

function describeEntryStructure(legs: StructureEntryCandidateLeg[]) {
  if (legs.length === 2) {
    const types = new Set(legs.map((leg) => leg.option_type?.toLowerCase()));
    const sides = new Set(legs.map((leg) => leg.side?.toUpperCase()));
    if (types.size === 1 && sides.has("LONG") && sides.has("SHORT")) {
      return types.has("call") ? "Call vertical" : types.has("put") ? "Put vertical" : "Vertical spread";
    }
  }
  if (legs.length === 4) {
    const putShort = legs.find((leg) => leg.option_type === "put" && leg.side === "SHORT");
    const putLong = legs.find((leg) => leg.option_type === "put" && leg.side === "LONG");
    const callShort = legs.find((leg) => leg.option_type === "call" && leg.side === "SHORT");
    const callLong = legs.find((leg) => leg.option_type === "call" && leg.side === "LONG");
    if (putShort && putLong && callShort && callLong) {
      if (putShort.strike === callShort.strike) return "Iron butterfly";
      if (Number(putLong.strike) < Number(putShort.strike) && Number(callShort.strike) < Number(callLong.strike)) return "Iron condor";
    }
  }
  return `${legs.length}-leg options setup`;
}

function getStructureEntrySpec(rawSpec?: WatchEvaluationSpec) {
  if (rawSpec?.subject !== "STRUCTURE_ENTRY") return null;
  const spec = rawSpec as StructureEntryEvaluationSpec;
  const candidate = spec.candidate;
  const legs = (candidate?.legs ?? [])
    .map((leg) => ({ ...leg, option_type: leg.option_type?.toLowerCase() }))
    .filter((leg) =>
      (leg.side === "LONG" || leg.side === "SHORT") &&
      (leg.option_type === "call" || leg.option_type === "put") &&
      Number.isFinite(Number(leg.strike)) && Number(leg.strike) > 0,
    );
  const target = spec.target;
  const threshold = Number(target?.threshold_per_share);
  if (!candidate?.ticker || !candidate.expiry || legs.length === 0 || !Number.isFinite(threshold) || threshold < 0) return null;
  if (target?.type !== "NET_DEBIT_LTE" && target?.type !== "NET_CREDIT_GTE") return null;
  return { candidate, legs, target: target as Required<StructureEntryEvaluationSpec>["target"] };
}

function getPositionExitSpec(rawSpec?: WatchEvaluationSpec) {
  if (rawSpec?.subject !== "POSITION_EXIT") return null;
  const spec = rawSpec as PositionExitEvaluationSpec;
  const triggers = Array.isArray(spec.triggers)
    ? spec.triggers.filter((trigger) => Boolean(trigger?.type))
    : [];
  if (triggers.length === 0) return null;
  return { logic: spec.logic === "AND" ? "AND" : "OR", triggers };
}

function formatThreshold(value: unknown, { absolute = false }: { absolute?: boolean } = {}) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value ?? "not set");
  const rendered = (absolute ? Math.abs(parsed) : parsed).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
  return rendered;
}

function formatPositionExitTrigger(trigger: PositionExitTrigger) {
  if (trigger.type === "pnl_usd") {
    return trigger.operator === "lte"
      ? `Estimated loss reaches $${formatThreshold(trigger.threshold, { absolute: true })}`
      : `Estimated profit reaches $${formatThreshold(trigger.threshold, { absolute: true })}`;
  }
  if (trigger.type === "max_profit_pct") {
    return `Profit reaches ${formatThreshold(trigger.threshold)}% of maximum profit`;
  }
  if (trigger.type === "max_loss_pct") {
    return `Loss reaches ${formatThreshold(trigger.threshold)}% of maximum loss`;
  }
  if (trigger.type === "dte") {
    const qualifier = trigger.operator === "eq" ? "exactly" : "or fewer";
    return `${formatThreshold(trigger.threshold)} days ${qualifier} remain until expiration`;
  }
  if (trigger.type === "short_strike_breach") {
    const side = trigger.side === "PUT" ? "put " : trigger.side === "CALL" ? "call " : "";
    return `Underlying breaches a short ${side}strike`;
  }
  return humanize(trigger.type);
}

function formatEntryTarget(spec: NonNullable<ReturnType<typeof getStructureEntrySpec>>) {
  const threshold = Number(spec.target.threshold_per_share).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
  return spec.target.type === "NET_CREDIT_GTE"
    ? `Net credit at or above $${threshold} per share`
    : `Net debit at or below $${threshold} per share`;
}

export default function WatchDetailPanel({ detail, loading, error, onRefresh, onDuplicate, onReplace, className }: WatchDetailPanelProps) {
  const [tab, setTab] = useState<"overview" | "activity" | "settings">("overview");
  const [openActivityItems, setOpenActivityItems] = useState<Record<string, boolean>>({});
  const tabId = useId();
  const tabs = ["overview", "activity", "settings"] as const;
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  if (loading) return <div className={`${styles.root} ${className ?? ""}`} role="status">Loading watch details…</div>;
  if (!detail) return <div className={`${styles.root} ${className ?? ""}`} role="alert">{error || "Watch details are unavailable."} <button onClick={onRefresh}>Retry</button></div>;
  const context = detail.monitoring_context;
  const position = detail.watch.position;
  const structureEntry = getStructureEntrySpec(detail.settings?.evaluation_spec);
  const positionExit = getPositionExitSpec(detail.settings?.evaluation_spec);
  const signalCondition = detail.settings?.signal_condition;
  const recipeCondition = detail.settings?.recipe_condition;
  const signalMeasurement = context.signal_measurement;
  const legQuotes = position?.valuation?.legs ?? [];
  const hasLegQuotes = legQuotes.some((leg) => leg.bid != null || leg.ask != null || leg.selected_mark != null || leg.delta != null);
  const canCopyPosition = Boolean(position && onDuplicate && onReplace && (detail.watch.lifecycle?.state === "ACTIVE" || detail.watch.lifecycle?.state === "PAUSED"));
  const selectTab = (nextIndex: number) => {
    const next = (nextIndex + tabs.length) % tabs.length;
    setTab(tabs[next]);
    tabRefs.current[next]?.focus();
  };

  return <section className={`${styles.root} ${className ?? ""}`} aria-label="Watch details">
    {error && <div role="alert">{error} <button onClick={onRefresh}>Retry</button></div>}
    <div className={styles.tabs} role="tablist" aria-label="Watch detail sections">
      {tabs.map((item, index) => <button ref={(node) => { tabRefs.current[index] = node; }} key={item} role="tab" tabIndex={tab === item ? 0 : -1} aria-selected={tab === item} aria-controls={`${tabId}-${item}`} id={`${tabId}-${item}-tab`} className={tab === item ? styles.tabActive : styles.tab} onClick={() => setTab(item)} onKeyDown={(event) => { if (event.key === "ArrowRight") { event.preventDefault(); selectTab(index + 1); } else if (event.key === "ArrowLeft") { event.preventDefault(); selectTab(index - 1); } else if (event.key === "Home") { event.preventDefault(); selectTab(0); } else if (event.key === "End") { event.preventDefault(); selectTab(tabs.length - 1); } }}>{item[0].toUpperCase() + item.slice(1)}</button>)}
    </div>
    <div className={styles.context} aria-label="Monitoring context">
      <div><span>Lifecycle</span><strong>{humanize(detail.watch.lifecycle?.state)}</strong></div>
      <div><span>Last evaluation</span><strong>{humanize(context.evaluation?.state)}</strong></div>
      <div><span>Data quality</span><strong>{humanize(context.data_quality?.state)}</strong></div>
      <div><span>Alert eligibility</span><strong>{humanize(context.alert_eligibility?.state)}</strong></div>
      <div><span>Delivery</span><strong>{humanize(context.delivery?.state)}</strong></div>
      <div><span>Next evaluation</span><strong>{formatTime(context.next_evaluation_at)}</strong></div>
    </div>
    {tab === "overview" && recipeCondition ? (
      <section className={styles.content} aria-labelledby={`${tabId}-recipe-condition`}>
        <h3 id={`${tabId}-recipe-condition`}>{recipeCondition.recipe?.name || "Guided recipe"}</h3>
        <p>{recipeCondition.sentence || recipeCondition.recipe?.summary}</p>
        <p><strong>All conditions must be true</strong></p>
        <ul>
          {recipeCondition.conditions?.map((condition, index) => (
            <li key={`${condition.sentence}-${index}`}>{condition.sentence || "Condition details unavailable"}</li>
          ))}
        </ul>
        {recipeCondition.evidence_time?.display ? <p><strong>Evidence timing:</strong> {recipeCondition.evidence_time.display}</p> : null}
        {recipeCondition.behavior ? <p>{recipeCondition.behavior}</p> : null}
      </section>
    ) : null}
    <div role="tabpanel" id={`${tabId}-${tab}`} aria-labelledby={`${tabId}-${tab}-tab`} className={styles.content}>
      {tab === "overview" && <div className={styles.overview}><h3>{context.evaluation?.summary || "Evaluation outcome is not available yet."}</h3>{detail.watch.lifecycle?.reason && <p role="status">{detail.watch.lifecycle.reason}</p>}<p>{context.data_quality?.summary}</p><p>{context.alert_eligibility?.reason}</p><dl><div><dt>Instrument</dt><dd>{detail.watch.instrument?.ticker || "—"}</dd></div><div><dt>Contract</dt><dd>{structureEntry ? "New options setup" : detail.watch.instrument?.option ? formatOption(detail.watch.instrument.option) : position ? "Held options position" : "Underlying watch"}</dd></div>{position?.strategy && <div><dt>Structure</dt><dd>{humanize(position.strategy)}</dd></div>}{structureEntry && <><div><dt>Structure</dt><dd>{describeEntryStructure(structureEntry.legs)}</dd></div><div><dt>Expiration</dt><dd>{formatExpiry(structureEntry.candidate.expiry)}</dd></div><div><dt>Entry target</dt><dd>{formatEntryTarget(structureEntry)}</dd></div></>}</dl>{signalMeasurement && <section aria-labelledby={`${tabId}-signal-measurement`}><h3 id={`${tabId}-signal-measurement`}>Latest signal reading</h3>{signalMeasurement.sentence && <p>{signalMeasurement.sentence}</p>}<dl>{Object.entries(signalMeasurement.metrics || {}).map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}{signalMeasurement.completed_session_display && <div><dt>Completed session</dt><dd>{signalMeasurement.completed_session_display}</dd></div>}</dl></section>}{structureEntry && <section aria-labelledby={`${tabId}-entry-setup`}><h3 id={`${tabId}-entry-setup`}>Setup to enter</h3><p>This Watch evaluates the opening price: offers for long legs and bids for short legs. It does not track a position you already hold.</p><div className={styles.positionTableWrap}><table><caption>{describeEntryStructure(structureEntry.legs)} candidate legs</caption><thead><tr><th scope="col">Action</th><th scope="col">Option</th><th scope="col">Quantity</th></tr></thead><tbody>{structureEntry.legs.map((leg, index) => <tr key={`${leg.side}-${leg.option_type}-${leg.strike}-${index}`}><td>{humanize(leg.side)}</td><td>{formatStrike(leg.strike)} {leg.option_type === "call" ? "Call" : "Put"}</td><td>{formatContractCount(leg.contracts)}</td></tr>)}</tbody></table></div></section>}{position && <section aria-labelledby={`${tabId}-position-quotes`}><h3 id={`${tabId}-position-quotes`}>Position quotes</h3><p>Entry basis is preserved separately from the latest market valuation. Fees are {position.fees_policy === "fees_excluded_v1" ? "excluded" : "not available"}.</p>{position.entry_basis && <div><h4>Net opening price</h4><p>{strategyBasisSummary(position) || "Opening basis unavailable."}</p></div>}{canCopyPosition && <div className={styles.copyActions}><p>Position legs and entry basis are permanent. Duplicate to retain this Watch, or replace it to start a new Watch and stop this one.</p><button type="button" onClick={onDuplicate}>Duplicate Watch</button><button type="button" onClick={onReplace}>Replace Watch</button></div>}{position.valuation ? <dl><div><dt>Entry cash flow</dt><dd>{position.valuation.entry_cash_flow_usd ?? "Unavailable"}</dd></div><div><dt>Current close cash flow</dt><dd>{position.valuation.current_close_cash_flow_usd ?? "Unavailable"}</dd></div><div><dt>Estimated P&amp;L</dt><dd>{position.valuation.unrealized_pnl_usd ?? "Unavailable"}</dd></div></dl> : <p>Current valuation is awaiting coherent market quotes.</p>}<div className={styles.positionTableWrap}><table><thead><tr><th scope="col">Side</th><th scope="col">Option</th><th scope="col">Contracts</th><th scope="col">Multiplier</th>{!position.entry_basis && <th scope="col">Entry premium</th>}{hasLegQuotes && <><th scope="col">Role</th><th scope="col">Bid / ask</th><th scope="col">Mark</th><th scope="col">Delta</th><th scope="col">Quote quality</th></>}</tr></thead><tbody>{(position.legs || []).map((leg, index) => { const quote = position.valuation?.legs?.find((item) => optionIdentityKey(item.option) === optionIdentityKey(leg.option)); return <tr key={`${optionIdentityKey(leg.option)}-${index}`}><td>{humanize(leg.side)}</td><td>{formatOption(leg.option)}</td><td>{leg.contracts ?? "—"}</td><td>{leg.multiplier ?? "—"}</td>{!position.entry_basis && <td>{leg.entry_premium_per_share ?? "—"}</td>}{hasLegQuotes && <><td>{quote?.role || "—"}</td><td>{quote?.bid != null && quote?.ask != null ? `${quote.bid} / ${quote.ask}` : "—"}</td><td>{quote?.selected_mark || "—"}</td><td>{quote?.delta || "—"}</td><td>{humanize(quote?.quote_state)}</td></>}</tr>; })}</tbody></table></div></section>}</div>}
      {tab === "activity" && <ol className={styles.activity}>{(detail.activity?.items || []).length === 0 ? <li>No activity recorded yet.</li> : detail.activity?.items?.map((item) => {
        const open = Boolean(openActivityItems[item.id]);
        const details = item.details;
        const detailRows: Array<{ label: string; state?: string; summary?: string; metrics?: Record<string, string>; lines?: string[] }> = [];
        if (details?.evaluation) detailRows.push({ label: "Outcome", state: details.evaluation.state, summary: details.evaluation.summary });
        if (details?.data_quality) detailRows.push({ label: "Evidence", state: details.data_quality.state, summary: details.data_quality.summary });
        if (details?.delivery) detailRows.push({ label: "Delivery", state: details.delivery.state, summary: details.delivery.summary });
        if (details?.measurement) {
          const measurement = details.measurement;
          detailRows.push({
            label: "Measurement",
            summary: measurement.sentence || (measurement.clauses
              ? `${measurement.conditions_met ?? 0} of ${measurement.clauses.length} conditions met`
              : measurement.indicator),
            metrics: measurement.metrics,
            lines: measurement.clauses?.map((clause) =>
              `${clause.clause_id || "condition"}: observed ${clause.observed_value ?? "?"}${clause.unit ? ` ${clause.unit}` : ""} (threshold ${clause.threshold ?? "?"}, ${clause.condition_met ? "met" : "not met"})`,
            ),
          });
        }
        return <li key={item.id}>
          <div className={styles.activityHead}>
            <time>{formatTime(item.occurred_at)}</time>
            <strong>{item.title}</strong>
            <span className={styles.activityState} data-tone={activityTone(item.state)}>{humanize(item.state)}</span>
          </div>
          <p>{item.description}</p>
          {details ? <>
            <button type="button" className={styles.activityToggle} aria-expanded={open} onClick={() => setOpenActivityItems((prev) => ({ ...prev, [item.id]: !open }))}>
              {open ? "Hide scan detail" : "View scan detail"}
            </button>
            {open && <dl className={styles.activityDetail}>
              {detailRows.map((row) => <div key={row.label}>
                <dt>{row.label}</dt>
                <dd>
                  {row.state && <span>{humanize(row.state)}</span>}
                  {row.summary && <span>{row.summary}</span>}
                  {Object.entries(row.metrics || {}).map(([key, value]) => <span key={key}>{key}: {value}</span>)}
                  {(row.lines || []).map((line) => <span key={line}>{line}</span>)}
                </dd>
              </div>)}
            </dl>}
          </> : null}
        </li>;
      })}</ol>}
      {tab === "settings" && <dl className={styles.settings}><div><dt>Intent</dt><dd>{structureEntry ? "Setup entry monitor" : signalCondition ? "Market monitor" : humanize(detail.settings?.intent)}</dd></div>{structureEntry ? <><div><dt>Entry target</dt><dd>{formatEntryTarget(structureEntry)}</dd></div><div><dt>Price convention</dt><dd>Long offers, short bids</dd></div><div><dt>Structure changes</dt><dd>Create a new Watch to monitor a different setup.</dd></div></> : signalCondition ? <><div><dt>Signal</dt><dd>{signalCondition.signal?.name || "Market condition"}</dd></div><div><dt>Alert</dt><dd>{signalCondition.sentence || "Condition details unavailable"}</dd></div></> : positionExit ? <><div><dt>Trigger logic</dt><dd>{positionExit.logic === "OR" ? "Alert when any trigger is met" : "Alert when all triggers are met"}</dd></div><div><dt>Triggers</dt><dd><ul>{positionExit.triggers.map((trigger, index) => <li key={trigger.id || `${trigger.type}-${index}`}>{formatPositionExitTrigger(trigger)}</li>)}</ul></dd></div></> : <><div><dt>Condition logic</dt><dd>{detail.settings?.condition_logic || "AND"}</dd></div><div><dt>Conditions</dt><dd>{detail.settings?.conditions?.length || 0} configured</dd></div></>}<div><dt>Evaluation interval</dt><dd>{String(detail.settings?.policy?.evaluation_interval_minutes || "—")} minutes</dd></div></dl>}
    </div>
  </section>;
}
